import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Minus, Plus } from "lucide-react";
import { useCart } from "../context/CartContext";
import { createCheckout, validatePromo, money } from "../lib/api";
import { toast } from "sonner";

export default function CartDrawer() {
  const { items, updateQty, remove, subtotal, open, setOpen, count } = useCart();
  const [loading, setLoading] = useState(false);
  const [promoInput, setPromoInput] = useState("");
  const [promo, setPromo] = useState(null); // {code, discount, freeShipping, message}
  const [promoError, setPromoError] = useState("");
  const [applying, setApplying] = useState(false);

  const payload = () => items.map((i) => ({ lookup_key: i.lookupKey, quantity: i.quantity, variantId: i.variantId || null }));

  const applyPromo = async () => {
    if (!promoInput.trim()) return;
    setApplying(true);
    setPromoError("");
    try {
      const res = await validatePromo({ code: promoInput.trim(), items: payload() });
      if (res.valid) {
        setPromo(res);
        setPromoError("");
      } else {
        setPromo(null);
        setPromoError(res.message || "This code is not valid.");
      }
    } catch {
      setPromoError("Could not check the code. Try again.");
    } finally {
      setApplying(false);
    }
  };

  const clearPromo = () => { setPromo(null); setPromoInput(""); setPromoError(""); };

  const checkout = async () => {
    setLoading(true);
    try {
      const { checkout_url } = await createCheckout(payload(), promo?.code);
      window.location.href = checkout_url;
    } catch (e) {
      const msg = e.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Could not start checkout. Try again.");
      setLoading(false);
    }
  };

  const estimatedTotal = Math.max(0, subtotal - (promo?.discount || 0));

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[9997]"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          data-testid="cart-drawer"
        >
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <motion.aside
            className="absolute top-0 right-0 h-full w-full max-w-md bg-[#080808] border-l border-white/10 flex flex-col"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="flex items-center justify-between px-8 h-20 border-b border-white/10">
              <span className="font-mono text-[11px] tracking-[0.3em] text-white/60">YOUR CART ({count})</span>
              <button onClick={() => setOpen(false)} data-testid="cart-close" aria-label="Close cart">
                <X size={20} className="text-white/60 hover:text-gold transition-colors" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-8 py-6">
              {items.length === 0 ? (
                <p className="font-serif text-2xl text-white/40 mt-20 text-center">Your cart is empty.</p>
              ) : (
                <ul className="space-y-8">
                  {items.map((i) => (
                    <li key={i.key} className="flex gap-4" data-testid={`cart-item-${i.lookupKey}`}>
                      <img src={i.artwork} alt={i.name} className="w-20 h-20 object-cover flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="font-mono text-[9px] tracking-[0.2em] text-gold">{i.subtitle?.toUpperCase()}</p>
                        <h4 className="font-serif text-xl text-white leading-tight truncate">{i.name}</h4>
                        {i.variantLabel && (
                          <p className="font-mono text-[9px] tracking-[0.15em] text-white/40 mt-1" data-testid={`cart-variant-${i.lookupKey}`}>
                            {i.variantLabel.toUpperCase()}
                          </p>
                        )}
                        <div className="flex items-center justify-between mt-3">
                          <div className="flex items-center border border-white/15">
                            <button
                              onClick={() => updateQty(i.key, i.quantity - 1)}
                              className="px-2 py-1 text-white/60 hover:text-gold"
                              data-testid={`cart-dec-${i.lookupKey}`}
                              aria-label="Decrease"
                            >
                              <Minus size={12} />
                            </button>
                            <span className="px-3 font-mono text-xs text-white">{i.quantity}</span>
                            <button
                              onClick={() => updateQty(i.key, i.quantity + 1)}
                              className="px-2 py-1 text-white/60 hover:text-gold"
                              data-testid={`cart-inc-${i.lookupKey}`}
                              aria-label="Increase"
                            >
                              <Plus size={12} />
                            </button>
                          </div>
                          <span className="font-mono text-sm text-white/80">{money(i.price * i.quantity, i.currency)}</span>
                        </div>
                        <button
                          onClick={() => remove(i.key)}
                          className="font-mono text-[9px] tracking-[0.2em] text-white/30 hover:text-red-500 mt-2 transition-colors"
                          data-testid={`cart-remove-${i.lookupKey}`}
                        >
                          REMOVE
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {items.length > 0 && (
              <div className="border-t border-white/10 px-8 py-6">
                {/* Promo code */}
                <div className="mb-5">
                  <p className="font-mono text-[9px] tracking-[0.25em] text-white/40 mb-2">PROMO CODE</p>
                  {promo ? (
                    <div className="flex items-center justify-between border border-gold/30 bg-gold/5 px-4 py-3" data-testid="promo-applied">
                      <div>
                        <span className="font-mono text-[11px] tracking-[0.2em] text-gold">{promo.code}</span>
                        <p className="font-mono text-[9px] text-white/45 mt-1">{promo.message}</p>
                      </div>
                      <button onClick={clearPromo} data-testid="promo-remove"
                        className="font-mono text-[9px] tracking-[0.15em] text-white/40 hover:text-red-400">REMOVE</button>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <input value={promoInput} onChange={(e) => setPromoInput(e.target.value.toUpperCase())}
                        onKeyDown={(e) => e.key === "Enter" && applyPromo()}
                        placeholder="Enter promo code" data-testid="promo-input"
                        className="flex-1 bg-transparent border border-white/15 focus:border-gold outline-none px-4 py-3 font-mono text-[11px] tracking-[0.15em] text-white placeholder:text-white/25 transition-colors" />
                      <button onClick={applyPromo} disabled={applying || !promoInput.trim()} data-testid="promo-apply"
                        className="border border-white/25 font-mono text-[10px] tracking-[0.25em] px-5 hover:border-gold hover:text-gold transition-colors disabled:opacity-40">
                        {applying ? "…" : "APPLY"}
                      </button>
                    </div>
                  )}
                  {promoError && (
                    <p className="font-mono text-[9px] tracking-[0.1em] text-red-400 mt-2" data-testid="promo-error">{promoError}</p>
                  )}
                </div>

                <div className="flex justify-between items-baseline mb-1">
                  <span className="font-mono text-[11px] tracking-[0.2em] text-white/50">SUBTOTAL</span>
                  <span className="font-serif text-2xl text-white">{money(subtotal)}</span>
                </div>
                {promo?.discount > 0 && (
                  <>
                    <div className="flex justify-between items-baseline mb-1" data-testid="promo-discount-row">
                      <span className="font-mono text-[10px] tracking-[0.2em] text-gold">DISCOUNT ({promo.code})</span>
                      <span className="font-mono text-sm text-gold">−{money(promo.discount)}</span>
                    </div>
                    <div className="flex justify-between items-baseline mb-2">
                      <span className="font-mono text-[11px] tracking-[0.2em] text-white/50">ESTIMATED TOTAL</span>
                      <span className="font-serif text-3xl text-white" data-testid="cart-estimated-total">{money(estimatedTotal)}</span>
                    </div>
                  </>
                )}
                <p className="font-mono text-[9px] tracking-[0.15em] text-white/30 mb-5">
                  TAXES &amp; SHIPPING CALCULATED AT CHECKOUT
                </p>
                <button
                  onClick={checkout}
                  disabled={loading}
                  data-testid="cart-checkout"
                  className="w-full bg-gold text-black font-mono text-[11px] tracking-[0.25em] py-4 hover:bg-white transition-colors disabled:opacity-50"
                >
                  {loading ? "OPENING CHECKOUT..." : "PROCEED TO CHECKOUT"}
                </button>
              </div>
            )}
          </motion.aside>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
